import { gql } from '@apollo/client';
import type { MediaType } from '@cobliteam/common-types';
import graphQLRequester from '../../request/GraphQLRequester';

/**
 * Quanto uma solicitação de vídeo costuma levar, agregado pelo pooler (VXP-934).
 *
 * A frota vem da sessão, então a query não leva argumento. O agregado é cacheado por 1 h no
 * servidor, por frota e restrição de grupo.
 */

export type DeviceDeliveryTime = {
  deviceId: string;
  mediaType: MediaType;
  /** Solicitações concluídas que sustentam a mediana. Quem consome decide o mínimo. */
  sampleSize: number;
  p50Ms: number;
};

export type FleetDeliveryTime = {
  mediaType: MediaType;
  sampleSize: number;
  p50Ms: number;
  /** Amostra própria: a janela do p90 termina 7 dias antes da janela da mediana. */
  p90SampleSize: number;
  /** Nulo quando essa janela não tem amostra — normal em frota nova, não erro. */
  p90Ms: number | null;
};

export type VideoDeliveryTime = {
  devices: DeviceDeliveryTime[];
  fleet: FleetDeliveryTime[];
};

export class GraphQLVideoDeliveryTimeRequester {
  // `graphQLRequester.query` resolve para `{ errors, ...data }` (errorPolicy 'all'), então dado
  // parcial chega junto com erro. Sem agregado é diferente de agregado vazio: o segundo é estado
  // desenhado, o primeiro não pode virar "essa frota não tem amostra".
  private assertNoErrors = (errors?: unknown[]) => {
    if (errors?.length) {
      throw { errors };
    }
  };

  private queryVideoDeliveryTime = gql`
    query videoDeliveryTime {
      videoDeliveryTime {
        devices {
          deviceId
          mediaType
          sampleSize
          p50Ms
        }
        fleet {
          mediaType
          sampleSize
          p50Ms
          p90SampleSize
          p90Ms
        }
      }
    }
  `;

  public getVideoDeliveryTime = (): Promise<VideoDeliveryTime> =>
    graphQLRequester
      .query<{ videoDeliveryTime: VideoDeliveryTime; errors?: unknown[] }>({
        query: this.queryVideoDeliveryTime,
      })
      .then((response) => {
        this.assertNoErrors(response.errors);
        return response.videoDeliveryTime;
      });
}

export default GraphQLVideoDeliveryTimeRequester;
